import { Link } from "react-router-dom";
import {
  canOpenRecord,
  opensWhenDeleted,
  recordUrl,
} from "constants/recordLinks";

export const RECORD_LINK_TONE = {
  PRIMARY: "primary",
  INHERIT: "inherit",
};

// AntD's global reset removes the outline of every anchor inside a table or popover.
const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary";

const DELETED_CLASS = "text-danger line-through";

const TONE_CLASS = {
  [RECORD_LINK_TONE.PRIMARY]: `text-primary hover:underline ${FOCUS_RING}`,
  [RECORD_LINK_TONE.INHERIT]: `hover:underline ${FOCUS_RING}`,
};

// A link inside a clickable table row or card must not also fire the row's own handler.
const stopPropagation = (event) => event.stopPropagation();

/**
 * RecordLink - A record's name (or serial) as a real link to its page in the ERP, so it can be
 * copied, opened in a new tab and middle-clicked. Renders the same children as plain text — never
 * a dead link — when the record has no page, the row came without an id, the record is deleted
 * (and its page 404s for deleted records), or the user lacks the permission the page is gated on.
 *
 * Never nest it inside another `<Link>` / `<a>`: anchors cannot contain anchors.
 *
 * @param {string} type - `RECORD_TYPE` value (a backend morph alias works as-is); an invoice row's
 *   page comes from `invoiceRecordType(row.type)`
 * @param {number|string} id - The record's id
 * @param {boolean} deleted - Soft-deleted record: shown struck through, and not linked unless its
 *   page still opens for deleted records (a customer's does — see `opensWhenDeleted`)
 * @param {string} tone - `RECORD_LINK_TONE.PRIMARY` (default) or `INHERIT` inside a chip/badge
 *   that sets its own colour
 * @param {string} title - Tooltip
 * @param {string} className - Appended, for layout and typography — not colour (use `tone`)
 */
const RecordLink = ({
  type,
  id,
  deleted = false,
  tone = RECORD_LINK_TONE.PRIMARY,
  title,
  className = "",
  children,
}) => {
  if (children == null || children === "") return null;

  const url = recordUrl(type, id);
  const canLink =
    url && canOpenRecord(type) && (!deleted || opensWhenDeleted(type));

  if (!canLink) {
    return (
      <span
        title={title}
        className={
          deleted
            ? `${DELETED_CLASS} ${className}`.trim()
            : className || undefined
        }
      >
        {children}
      </span>
    );
  }

  const look = deleted
    ? `${DELETED_CLASS} ${FOCUS_RING}`
    : (TONE_CLASS[tone] ?? TONE_CLASS[RECORD_LINK_TONE.PRIMARY]);

  return (
    <Link
      to={url}
      title={title}
      onClick={stopPropagation}
      className={`${look} ${className}`.trim()}
    >
      {children}
    </Link>
  );
};

export default RecordLink;
